<script setup lang="ts">
import log from "loglevel";
import { computed, defineAsyncComponent, onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";

import DappLoader from "@/components/DappLoader";
import Error from "@/components/Error";
import PasswordlessHandler from "@/handlers/AbstractHandler";
import { LOGIN_CONNECTIONS } from "@/utils/constants";
import { EMAIL_FLOW, EMAIL_FLOW_TYPE } from "@/utils/interfaces";

interface IError {
  message?: string;
  subMessage?: string;
}

const route = useRoute();
const isLoading = ref(true);
const error = ref<IError | undefined>(undefined);

const SmsAsyncComponent = defineAsyncComponent(() => import("@/components/SmsPasswordless"));
const EmailAsyncComponent = defineAsyncComponent(() => import("@/components/EmailPasswordless"));
const AuthenticatorAsyncComponent = defineAsyncComponent(() => import("@/components/Authenticator"));

const loginHint = computed(() => decodeURIComponent(route.query.login_hint as string));
const connection = computed(() => decodeURIComponent(route.query.connection as string));
const flowType = computed(() => decodeURIComponent((route.query.flow_type as string) || EMAIL_FLOW.LINK) as EMAIL_FLOW_TYPE);

onBeforeMount(async () => {
  if (!loginHint.value && connection.value !== LOGIN_CONNECTIONS.AUTHENTICATOR) {
    error.value = {
      message: "passwordless.start-something-wrong-error",
      subMessage: "passwordless.something-went-wrong-subheading",
    };
  }
  const handler = new PasswordlessHandler(window.location.href);
  try {
    await handler.validteRedirectUri();
  } catch (err: unknown) {
    log.error(err);
    error.value = {
      message: "Invalid redirect uri",
      subMessage: (err as Error).message,
    };
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div v-if="isLoading" class="flex flex-col items-center justify-center text-center">
    <DappLoader />
  </div>

  <div v-else-if="error" class="flex flex-col items-center justify-center text-center">
    <Error :heading="error.message" :subheading="error.subMessage" />
  </div>

  <div v-else>
    <Suspense v-if="connection === LOGIN_CONNECTIONS.SMS">
      <SmsAsyncComponent :connection="connection" :login-hint="loginHint" />
      <template #fallback>
        <DappLoader />
      </template>
    </Suspense>
    <Suspense v-if="connection === LOGIN_CONNECTIONS.EMAIL">
      <EmailAsyncComponent :connection="connection" :login-hint="loginHint" :flow-type="flowType" />
      <template #fallback>
        <DappLoader />
      </template>
    </Suspense>
    <Suspense v-if="connection === LOGIN_CONNECTIONS.AUTHENTICATOR">
      <AuthenticatorAsyncComponent :connection="connection" />
      <template #fallback>
        <DappLoader />
      </template>
    </Suspense>
  </div>
</template>
